import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { RoleGuard } from 'src/guards/role-guard';

import { Layout as DashboardLayout } from 'src/layouts/dashboard';

const UserPage = lazy(() => import('src/pages/dashboard/users'));

const AnalyticsSeacePage = lazy(() => import('src/pages/dashboard/analytics/seace'));
const AnalyticsMiviviendaPage = lazy(() => import('src/pages/dashboard/analytics/mivivienda'));

const AccountPage = lazy(() => import('src/pages/dashboard/account'));

const OperacionesTablero = lazy(() => import('src/pages/dashboard/operaciones/tablero'));
const OperacionesInversionistas = lazy(
  () => import('src/pages/dashboard/operaciones/inversionistas')
);
const OperacionesPymes = lazy(() => import('src/pages/dashboard/operaciones/pymes'));
const OperacionesHonorarios = lazy(() => import('src/pages/dashboard/operaciones/honorarios'));
const OperacionesCierre = lazy(() => import('src/pages/dashboard/operaciones/cierre'));
const OperacionesArchivos = lazy(() => import('src/pages/dashboard/operaciones/archivos'));
const OperacionesCronograma = lazy(() => import('src/pages/dashboard/operaciones/cronograma'));
const OperacionesComunicaciones = lazy(
  () => import('src/pages/dashboard/operaciones/comunicaciones')
);
const OperacionesEstructuracion = lazy(
  () => import('src/pages/dashboard/operaciones/estructuracion')
);
const OperacionesLlamadoCapital = lazy(
  () => import('src/pages/dashboard/operaciones/llamado-capital')
);
const OperacionesSeguimiento = lazy(() => import('src/pages/dashboard/operaciones/seguimiento'));
const OperacionesFiduciaria = lazy(() => import('src/pages/dashboard/operaciones/fiduciaria'));
const OperacionesTiposInversion = lazy(
  () => import('src/pages/dashboard/operaciones/tipos-inversion')
);
const OperacionesAlimentar = lazy(() => import('src/pages/dashboard/operaciones/alimentar'));

const InversionistaDashboard = lazy(() => import('src/pages/dashboard/inversionista/dashboard'));
const InversionistaOportunidades = lazy(
  () => import('src/pages/dashboard/inversionista/oportunidades')
);
const InversionistaMisInversiones = lazy(
  () => import('src/pages/dashboard/inversionista/mis-inversiones')
);
const InversionistaHistorial = lazy(() => import('src/pages/dashboard/inversionista/historial'));
const InversionistaEstadoCuenta = lazy(
  () => import('src/pages/dashboard/inversionista/estado-cuenta')
);
const InversionistaArchivos = lazy(() => import('src/pages/dashboard/inversionista/archivos'));

const PymeDashboard = lazy(() => import('src/pages/dashboard/pyme/dashboard'));
const PymeSolicitar = lazy(() => import('src/pages/dashboard/pyme/solicitar'));
const PymeCotizador = lazy(() => import('src/pages/dashboard/pyme/cotizador'));
const PymeCronograma = lazy(() => import('src/pages/dashboard/pyme/cronograma'));
const PymeFacturacion = lazy(() => import('src/pages/dashboard/pyme/facturacion'));
const PymeContrato = lazy(() => import('src/pages/dashboard/pyme/contrato'));
const PymeAlertas = lazy(() => import('src/pages/dashboard/pyme/alertas'));
const PymeHonorarios = lazy(() => import('src/pages/dashboard/pyme/honorarios'));
const PymeCierre = lazy(() => import('src/pages/dashboard/pyme/cierre'));
const PymeArchivos = lazy(() => import('src/pages/dashboard/pyme/archivos'));
const ReportesPage = lazy(() => import('src/pages/dashboard/reportes'));

const AdminDashboard = lazy(() => import('src/pages/dashboard/admin'));
const AdminPermisos = lazy(() => import('src/pages/dashboard/admin/permisos'));

const garanteGuard = (element) => <RoleGuard allowedRoles={['garante']}>{element}</RoleGuard>;

const inversionistaGuard = (element) => (
  <RoleGuard allowedRoles={['inversionista']}>{element}</RoleGuard>
);

const pymeGuard = (element) => <RoleGuard allowedRoles={['pyme']}>{element}</RoleGuard>;

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <DashboardLayout>
        <Suspense>
          <Outlet />
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        path: 'users',
        element: (
          <RoleGuard
            allowedRoles={['garante']}
            allowedSubRoles={['administrador']}
          >
            <UserPage />
          </RoleGuard>
        ),
      },
      {
        path: 'account',
        element: <AccountPage />,
      },
      {
        path: 'analytics',
        children: [
          {
            path: 'seace',
            element: (
              <RoleGuard
                allowedRoles={['garante']}
                allowedSubRoles={['administrador', 'operador_1']}
              >
                <AnalyticsSeacePage />
              </RoleGuard>
            ),
          },
          {
            path: 'mivivienda',
            element: (
              <RoleGuard
                allowedRoles={['garante']}
                allowedSubRoles={['administrador', 'operador_2']}
              >
                <AnalyticsMiviviendaPage />
              </RoleGuard>
            ),
          },
        ],
      },
      {
        path: 'operaciones',
        children: [
          { path: 'tablero', element: garanteGuard(<OperacionesTablero />) },
          { path: 'inversionistas', element: garanteGuard(<OperacionesInversionistas />) },
          { path: 'pymes', element: garanteGuard(<OperacionesPymes />) },
          { path: 'honorarios', element: garanteGuard(<OperacionesHonorarios />) },
          { path: 'cierre', element: garanteGuard(<OperacionesCierre />) },
          { path: 'archivos', element: garanteGuard(<OperacionesArchivos />) },
          { path: 'cronograma', element: garanteGuard(<OperacionesCronograma />) },
          { path: 'comunicaciones', element: garanteGuard(<OperacionesComunicaciones />) },
          { path: 'estructuracion', element: garanteGuard(<OperacionesEstructuracion />) },
          { path: 'llamado-capital', element: garanteGuard(<OperacionesLlamadoCapital />) },
          { path: 'seguimiento', element: garanteGuard(<OperacionesSeguimiento />) },
          { path: 'fiduciaria', element: garanteGuard(<OperacionesFiduciaria />) },
          { path: 'tipos-inversion', element: garanteGuard(<OperacionesTiposInversion />) },
          { path: 'alimentar', element: garanteGuard(<OperacionesAlimentar />) },
          { path: 'reportes', element: garanteGuard(<ReportesPage />) },
        ],
      },
      {
        path: 'inversionista',
        children: [
          { path: 'dashboard', element: inversionistaGuard(<InversionistaDashboard />) },
          { path: 'oportunidades', element: inversionistaGuard(<InversionistaOportunidades />) },
          { path: 'mis-inversiones', element: inversionistaGuard(<InversionistaMisInversiones />) },
          { path: 'historial', element: inversionistaGuard(<InversionistaHistorial />) },
          { path: 'archivos', element: inversionistaGuard(<InversionistaArchivos />) },
          { path: 'estado-cuenta', element: inversionistaGuard(<InversionistaEstadoCuenta />) },
          {
            path: 'estado-cuenta/:operacionId',
            element: inversionistaGuard(<InversionistaEstadoCuenta />),
          },
          { path: 'reportes', element: inversionistaGuard(<ReportesPage />) },
        ],
      },
      {
        path: 'pyme',
        children: [
          { path: 'dashboard', element: pymeGuard(<PymeDashboard />) },
          { path: 'solicitar', element: pymeGuard(<PymeSolicitar />) },
          { path: 'cotizador', element: pymeGuard(<PymeCotizador />) },
          { path: 'cronograma', element: pymeGuard(<PymeCronograma />) },
          { path: 'facturacion', element: pymeGuard(<PymeFacturacion />) },
          { path: 'contrato', element: pymeGuard(<PymeContrato />) },
          { path: 'alertas', element: pymeGuard(<PymeAlertas />) },
          { path: 'honorarios', element: pymeGuard(<PymeHonorarios />) },
          { path: 'cierre', element: pymeGuard(<PymeCierre />) },
          { path: 'archivos', element: pymeGuard(<PymeArchivos />) },
          { path: 'reportes', element: pymeGuard(<ReportesPage />) },
        ],
      },
      {
        path: 'admin',
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'permisos', element: <AdminPermisos /> },
        ],
      },
    ],
  },
];
